import {
  CheckCircle,
  CircleOutlined,
  DoNotDisturb,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  Button,
  Collapse,
  LinearProgress,
  List,
  ListItem,
  Paper,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ChecklistTask } from "../../common/ChecklistTask";
import {
  useGetChecklistQuery,
  useCompleteChecklistMutation,
  useReopenChecklistMutation,
  useMarkNotApplicableMutation,
  useUnmarkNotApplicableMutation,
} from "../../redux/api/checklist";

interface ChecklistDisplayProps {
  root: ChecklistTask;
  userId: string | undefined;
  requiredCourseNames?: Set<string>;
  blockNaCourseNames?: Set<string>;
}

const ChecklistDisplay = (props: ChecklistDisplayProps) => {
  const { requiredCourseNames, blockNaCourseNames } = props;
  const checklistId = props.root.id;
  const [expanded, setExpanded] = useState<{
    [fieldName: number]: boolean;
  }>({});
  const [subTasks, setSubTasks] = useState<ChecklistTask[]>([]);

  const {
    data: checklist,
    isLoading,
    isFetching,
    refetch,
  } = useGetChecklistQuery(checklistId as number);
  const [completeChecklist] = useCompleteChecklistMutation();
  const [reopenChecklist] = useReopenChecklistMutation();
  const [markNotApplicable] = useMarkNotApplicableMutation();
  const [unmarkNotApplicable] = useUnmarkNotApplicableMutation();

  useEffect(() => {
    if (!isFetching && !isLoading) {
      setSubTasks(
        !!checklist?.subTasks && checklist?.subTasks.length > 0
          ? [...checklist?.subTasks]
          : []
      );
    }
  }, [checklist?.subTasks, isFetching, isLoading]);

  const postMarkNA = async (task: ChecklistTask) => {
    if (!props.userId) {
      toast.error(
        <p>
          Error submitting User ID, please try again. If the issue persists, try
          reloading the page or signing in again.
        </p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
      return;
    }
    const markResult = await markNotApplicable({
      checklistId: task.id as number,
      userId: props.userId as string,
    });
    if ("error" in markResult === false) {
      refetch();
    }
  }

  const postUnmarkNA = async (task: ChecklistTask) => {
    const unmarkResult = await unmarkNotApplicable(task.id as number);
    if ("error" in unmarkResult === false) {
      refetch();
    }
  }

  const postComplete = async (task: ChecklistTask) => {
    if (!props.userId) {
      toast.error(
        <p>
          Error submitting User ID, please try again. If the issue persists, try
          reloading the page or signing in again.
        </p>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
      return;
    }
    const completeResult = await completeChecklist({
      checklistId: task.id as number,
      userId: props.userId as string,
    });
    if ("error" in completeResult === false) {
      refetch();
    }
  };

  const postReopen = async (task: ChecklistTask) => {
    const reopenResult = await reopenChecklist(task.id as number);
    if ("error" in reopenResult === false) {
      refetch();
    }
  };

  return (
    <>
      {!isLoading && !!subTasks && subTasks.length > 0 && (
        <Paper className="checklist">
          <div className="progress-header">
            <h3>Progress</h3>
            {`${subTasks.filter((task) => task.completed || task.notApplicable).length} / ${
              subTasks.length
            }`}
          </div>
          <LinearProgress
            variant="determinate"
            color="success"
            value={
              (subTasks.filter((task) => task.completed || task.notApplicable).length /
                subTasks.length) *
              100
            }
            sx={{ height: "8px" }}
          />
          <List className="progress-list">
            {subTasks
              .sort((a, b) => {
                // sort tasks by position to display them in the right order
                if (a.position && b.position) {
                  return a.position > b.position ? 1 : -1;
                } else if (b.position) {
                  return -1;
                } else {
                  return 1;
                }
              })
              .map((task) => {
                return (
                  <ListItem key={task.id} className="progress-item-wrap">
                    <div className="progress-item">
                      {task.completed ? (
                        <Button onClick={() => postReopen(task)} disabled={task.notApplicable}>
                          <CheckCircle color="success" />
                        </Button>
                      ) : (
                        !!task.notApplicable ? (
                          <Button disabled={task.notApplicable}>
                            <CheckCircle color="disabled" />
                          </Button>
                        ) : (
                          <Button onClick={() => postComplete(task)} disabled={task.notApplicable}>
                            <CircleOutlined color="disabled" />
                          </Button>
                        )
                        
                      )}
                      <p className={!task.notApplicable ? "progress-description" : "progress-description na"}>{task.description}</p>
                      {!!task.subTasks && task.subTasks?.length > 0 && (
                        <div className="progress-right">
                          <p
                            className={
                              (task.subTasksCompletedCount || 0) + (task.subTasksNotApplicableCount || 0) === task.subTasksCount
                                ? "completed"
                                : ""
                            }
                          >
                            {(task.subTasksCompletedCount || 0) + (task.subTasksNotApplicableCount || 0)}/{task.subTasksCount}
                          </p>
                          {!!task.id && expanded[task.id] ? (
                            <ExpandLess
                              onClick={() => {
                                let temp = expanded;
                                if (!!task.id) {
                                  temp[task.id] = false;
                                  setExpanded(temp);
                                  //Doesn't make any real changes is just used to refresh checklist and display changes
                                  const check = JSON.parse(
                                    JSON.stringify(subTasks)
                                  );
                                  setSubTasks(check);
                                }
                              }}
                            />
                          ) : (
                            <ExpandMore
                              onClick={() => {
                                let temp = expanded;
                                if (!!task.id) {
                                  temp[task.id] = true;
                                  setExpanded(temp);
                                  //Doesn't make any real changes is just used to refresh checklist and display changes
                                  const check = JSON.parse(
                                    JSON.stringify(subTasks)
                                  );
                                  setSubTasks(check);
                                }
                              }}
                            />
                          )}
                        </div>
                      )}
                      {!task.notApplicable ? (
                        <Tooltip title="Mark as N/A" placement="right">
                          <Button className="light-link-button icon-button" onClick={() => postMarkNA(task)}>
                            <DoNotDisturb />
                          </Button>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Undo mark as N/A" placement="right">
                          <Button className="blue-link-button icon-button" onClick={() => postUnmarkNA(task)}>
                            <DoNotDisturb />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                    {task.subTasks && task.subTasks.length > 0 && (
                      <Collapse
                        in={!!task.id && !!expanded && !!expanded[task.id]}
                      >
                        <List component="div" disablePadding>
                          {task.subTasks.map((subTask) => (
                            <ListItem
                              component="div"
                              key={subTask.id}
                              className="sub-task"
                            >
                              {subTask.completed ? (
                                <Button onClick={() => postReopen(subTask)} disabled={subTask.notApplicable}>
                                  <CheckCircle color="success" />
                                </Button>
                              ) : (
                                !!subTask.notApplicable ? (
                                  <Button disabled={subTask.notApplicable}>
                                    <CheckCircle color="disabled" />
                                  </Button>
                                ) : (
                                  <Button onClick={() => postComplete(subTask)} disabled={subTask.notApplicable}>
                                    <CircleOutlined color="disabled" />
                                  </Button>
                                )
                              )}
                              <span className={!subTask.notApplicable ? "" : "na"}>
                                {subTask.description}
                                {!!requiredCourseNames?.has(subTask.description ?? "") && (
                                  <span style={{ color: "red" }}> *</span>
                                )}
                              </span>
                              {!subTask.notApplicable && !blockNaCourseNames?.has(subTask.description ?? "") ? (
                                <Tooltip title="Mark as N/A" placement="right">
                                  <Button className="light-link-button icon-button" onClick={() => postMarkNA(subTask)}>
                                    <DoNotDisturb />
                                  </Button>
                                </Tooltip>
                              ) : subTask.notApplicable ? (
                                <Tooltip title="Undo mark as N/A" placement="right">
                                  <Button className="blue-link-button icon-button" onClick={() => postUnmarkNA(subTask)}>
                                    <DoNotDisturb />
                                  </Button>
                                </Tooltip>
                              ) : null}
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </ListItem>
                );
              })}
          </List>
        </Paper>
      )}
    </>
  );
};

export default ChecklistDisplay;
